// Detects contact-information shaped text. Used to keep contact info OUT of
// free-text fields (bid messages, item descriptions). Contact details are only
// shared with the other party AFTER payment is authorised, so leaking them in
// these fields would bypass that policy.

// Phone numbers — sequence of 6+ digits, possibly separated by spaces / dashes
// / dots / parens, optional leading +. Matches:
//   "087 123 4567" / "087-123-4567" / "(087) 123 4567" / "+353 87 123 4567"
//   "0871234567" / "+44 20 7946 0958"
// Does NOT match quantities like "20 pallets", "100 cushions", "1200 flyers"
// because none of those have a run of 6+ digits (with separators) in a row.
const PHONE_RE = /\+?\d(?:[\s\-.()]*\d){5,}/;

// Emails — standard RFC-ish shape.
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;

// Website URLs — explicit protocols / www. or bare-domain TLDs.
const URL_RE =
  /\b(?:(?:https?:\/\/|www\.)\S+|[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,30}[a-zA-Z0-9])?\.(?:com|ie|co\.uk|net|org|io|uk|eu|me|app|dev|gov|edu|info|biz|tv|xyz))\b/i;

// Social handles — @username (2+ chars). Skip emails (preceded by a word char).
const HANDLE_RE = /(?:^|[^A-Za-z0-9_])@[A-Za-z0-9_]{2,}/;

export function containsPhoneNumber(text) {
  if (!text) return false;
  return PHONE_RE.test(String(text));
}

export function containsEmail(text) {
  if (!text) return false;
  return EMAIL_RE.test(String(text));
}

export function containsUrl(text) {
  if (!text) return false;
  return URL_RE.test(String(text));
}

export function containsSocialHandle(text) {
  if (!text) return false;
  return HANDLE_RE.test(String(text));
}

/**
 * Returns true if the text contains any disallowed contact information:
 * phone number, email, URL, or @social-handle.
 */
export function containsContactInfo(text) {
  if (!text) return false;
  return (
    containsPhoneNumber(text) ||
    containsEmail(text) ||
    containsUrl(text) ||
    containsSocialHandle(text)
  );
}

export const PHONE_BLOCK_MSG_BID =
  "Please don't include phone numbers in your bid message — your contact details are shared with the customer automatically once they accept and pay.";

export const PHONE_BLOCK_MSG_DESC =
  "Please don't include phone numbers in the item description — your contact details are shared with the driver automatically after payment.";

export const CONTACT_BLOCK_MSG_DESC =
  "Contact details are not permitted in the description. Driver and customer details are shared automatically after booking.";

export const DESCRIPTION_HELPER_TEXT =
  "Do not include phone numbers, email addresses, website links, or social media usernames. Contact details are automatically shared with the driver after payment.";

export const DESCRIPTION_MIN = 20;
export const DESCRIPTION_MAX = 250;

/**
 * Validates a delivery description. Returns null if valid, otherwise a
 * human-readable error string. Designed for live inline validation (the
 * length check is suppressed while the field is empty so we don't shout
 * at the user before they've started typing).
 */
export function validateDescription(text) {
  const value = String(text || '').trim();
  if (value.length > 0 && value.length < DESCRIPTION_MIN) {
    return `Description must be at least ${DESCRIPTION_MIN} characters.`;
  }
  if (value.length > DESCRIPTION_MAX) {
    return `Description must be ${DESCRIPTION_MAX} characters or fewer.`;
  }
  if (containsContactInfo(value)) {
    return CONTACT_BLOCK_MSG_DESC;
  }
  return null;
}
